import { FC, ReactNode } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';

import {
  BaseTimePickerProps,
  DateTimeSelectField,
  DateTimeSelectFieldProps,
  TimePicker,
  VehicleMakeAutocomplete,
  VehicleModelAutocomplete,
} from '@/components';
import { FormCurrencyField } from '@/components/FormElements/CurrencyInputField/CurrencyInputField';

import { AlertInfo, AlertInfoProps } from './AlertInfo';
import { FormAddressField, FormAddressFieldProps } from './FormAddressField';
import {
  FormAutocompleteField,
  FormAutocompleteFieldProps,
} from './FormAutocompleteField';
import { FormCheckboxField, FormCheckboxFieldProps } from './FormCheckboxField';
import { FormDateField, DatePickersFormFieldProps } from './FormDateField';
import { FormDateTimeField } from './FormDateTimeField';
import { FormFilePicker, FormFilePickerProps } from './FormFilePicker';
import {
  FormHeaderPositionToggle,
  FormHeaderPositionToggleProps,
} from './FormHeaderPositionToggle';
import { FormInputField, FormInputFieldProps } from './FormInputField';
import { FormIntegerField } from './FormIntegerField';
import { FormPasswordField } from './FormPasswordField';
import { FormPatternField, FormPatternFieldProps } from './FormPatternField';
import { FormRadiosField, FormRadiosFieldProps } from './FormRadiosField';
import { FormRangeField, FormRangeFieldProps } from './FormRangeField';
import { FormSelectField, FormSelectFieldProps } from './FormSelectField';
import { FormSignatureField } from './FormSignatureField';
import { FormSSNField, FormSSNFieldProps } from './FormSSNField';
import { FormSwitchField, FormSwitchFieldProps } from './FormSwitchField';
import { FormTabField, FormTabFieldProps } from './FormTabField';
import { FormTextAreaField } from './FormTextAreaField/FormTextAreaField';
import {
  FormTextEditorField,
  FormTextEditorFieldProps,
} from './FormTextEditorField';

interface IVehicleMakeFieldProps {
  type: 'autocomplete';
  name: 'make';
  label: string;
  value?: string;
  vehicleType?: string;
  handleChange: (name: 'make', value: { name: string; _id?: string }) => void;
  sx?: any;
  disabled?: boolean;
  items?: { label: string; value: string; _id?: string }[];
}

interface IVehicleModelFieldProps {
  type: 'autocomplete';
  name: 'model';
  label: string;
  value?: string;
  makeId?: string;
  makeName?: string;
  year?: number;
  disabled?: boolean;
  vehicleType?: string;
  handleChange: (name: 'model', value: { name: string; _id?: string }) => void;
  sx?: any;
  items?: { label: string; value: string; _id?: string }[];
}

export type IRenderFormFieldProps =
  | IVehicleMakeFieldProps
  | IVehicleModelFieldProps
  | (FormInputFieldProps & { [key: string]: any })
  | (FormSelectFieldProps & { [key: string]: any })
  | (DatePickersFormFieldProps & { [key: string]: any })
  | (FormPatternFieldProps & { [key: string]: any })
  | (FormCheckboxFieldProps & { [key: string]: any })
  | (FormSwitchFieldProps & { [key: string]: any })
  | (FormHeaderPositionToggleProps & { [key: string]: any })
  | (FormAutocompleteFieldProps & { [key: string]: any })
  | (FormAddressFieldProps & { [key: string]: any })
  | (FormFilePickerProps & { [key: string]: any })
  | (FormSSNFieldProps & { [key: string]: any })
  | (FormTabFieldProps & { [key: string]: any })
  | (FormRangeFieldProps & { [key: string]: any })
  | (FormRadiosFieldProps & { [key: string]: any })
  | (FormTextEditorFieldProps & { [key: string]: any })
  | (AlertInfoProps & { [key: string]: any })
  | (BaseTimePickerProps & { [key: string]: any })
  | (DateTimeSelectFieldProps & { [key: string]: any })
  | { type?: string; name?: string; [key: string]: any }; // Catch-all for any other props

const themeAwareSx = { color: 'text.primary' };

export const RenderFormField: FC<IRenderFormFieldProps> = (props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const name = 'name' in props ? props.name : undefined;
  const type = 'type' in props ? props.type : undefined;

  if (name === 'make' && type === 'autocomplete') {
    const makeProps = props as IVehicleMakeFieldProps;

    // If handleChange is provided, use regular FormAutocompleteField like MasterVehicleForm
    if (makeProps.handleChange) {
      return (
        <FormAutocompleteField
          {...makeProps}
          items={makeProps.items || []}
          freeSolo={true}
          addNew={true}
          handleChange={(fieldName: string, value: string | string[]) => {
            const stringValue = Array.isArray(value) ? value[0] : value;
            makeProps.handleChange(fieldName as 'make', {
              name: stringValue,
            });
          }}
        />
      );
    }

    // Otherwise use specialized component
    return (
      <Controller
        name={makeProps.name}
        control={control}
        defaultValue={makeProps.value || ''}
        render={({ field }) => {
          const stringValue =
            typeof field.value === 'string'
              ? field.value
              : field.value?.name || '';

          return (
            <VehicleMakeAutocomplete
              label={makeProps.label}
              value={stringValue}
              onChange={(selectedOption) => {
                field.onChange(selectedOption.name);
                if (makeProps.handleChange) {
                  makeProps.handleChange('make', selectedOption);
                }
              }}
              vehicleType={makeProps.vehicleType}
              sx={{ ...themeAwareSx, ...makeProps.sx }}
              disabled={makeProps.disabled}
            />
          );
        }}
      />
    );
  }

  if (name === 'model' && type === 'autocomplete') {
    const modelProps = props as IVehicleModelFieldProps;

    // If handleChange is provided, use regular FormAutocompleteField like MasterVehicleForm
    if (modelProps.handleChange) {
      return (
        <FormAutocompleteField
          {...modelProps}
          items={modelProps.items || []}
          freeSolo={true}
          addNew={true}
          handleChange={(fieldName: string, value: string | string[]) => {
            const stringValue = Array.isArray(value) ? value[0] : value;
            modelProps.handleChange(fieldName as 'model', {
              name: stringValue,
            });
          }}
        />
      );
    }

    // Otherwise use specialized component
    return (
      <Controller
        name={modelProps.name}
        control={control}
        defaultValue={modelProps.value || ''}
        render={({ field }) => {
          const stringValue =
            typeof field.value === 'string'
              ? field.value
              : field.value?.name || '';

          return (
            <VehicleModelAutocomplete
              label={modelProps.label}
              value={stringValue}
              makeId={modelProps.makeId}
              makeName={modelProps.makeName}
              year={modelProps.year}
              disabled={modelProps.disabled}
              onChange={(selectedOption) => {
                field.onChange(selectedOption.name);
                if (modelProps.handleChange) {
                  modelProps.handleChange('model', selectedOption);
                }
              }}
              vehicleType={modelProps.vehicleType}
              sx={{ ...themeAwareSx, ...modelProps.sx }}
            />
          );
        }}
      />
    );
  }

  switch (type) {
    case 'select':
      return <FormSelectField {...(props as FormSelectFieldProps)} />;
    case 'date':
      return <FormDateField {...(props as DatePickersFormFieldProps)} />;
    case 'datetime':
      return <FormDateTimeField {...(props as DatePickersFormFieldProps)} />;
    case 'datetimeselect':
      return <DateTimeSelectField {...(props as DateTimeSelectFieldProps)} />;
    case 'time': {
      const timeProps = props as BaseTimePickerProps & {
        name: string;
        rules?: any;
        defaultValue?: any;
        label?: ReactNode;
      };
      const error = get(errors, timeProps.name) as FieldError | undefined;
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Controller
            name={timeProps.name}
            control={control}
            defaultValue={timeProps.defaultValue ?? null}
            rules={timeProps.rules}
            render={({ field }) => (
              <TimePicker
                label={timeProps.label ?? ''}
                {...field}
                {...timeProps}
                value={field.value ? moment(field.value) : null}
                onChange={(newValue: moment.Moment | null) =>
                  field.onChange(newValue)
                }
                error={error?.message || ''}
                sx={{ ...themeAwareSx, ...(timeProps as any).sx }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }
    case 'pattern':
      return <FormPatternField {...(props as FormPatternFieldProps)} />;
    case 'password':
      return <FormPasswordField {...(props as FormInputFieldProps)} />;
    case 'checkbox':
      return <FormCheckboxField {...(props as FormCheckboxFieldProps)} />;
    case 'switch':
      return <FormSwitchField {...(props as FormSwitchFieldProps)} />;
    case 'autocomplete':
      return (
        <FormAutocompleteField {...(props as FormAutocompleteFieldProps)} />
      );
    case 'address':
      return <FormAddressField {...(props as FormAddressFieldProps)} />;
    case 'file':
      return <FormFilePicker {...(props as FormFilePickerProps)} />;
    case 'tab':
      return <FormTabField {...(props as FormTabFieldProps)} />;
    case 'integer':
      return <FormIntegerField {...(props as FormInputFieldProps)} />;
    case 'number':
      return (
        <FormInputField {...(props as FormInputFieldProps)} type="number" />
      );
    case 'ssn':
      return <FormSSNField {...(props as FormSSNFieldProps)} />;
    case 'range':
      return <FormRangeField {...(props as FormRangeFieldProps)} />;
    case 'signature':
      return <FormSignatureField {...(props as FormInputFieldProps)} />;
    case 'radios':
      return <FormRadiosField {...(props as FormRadiosFieldProps)} />;
    case 'text-editor':
      return <FormTextEditorField {...(props as FormTextEditorFieldProps)} />;
    case 'text-area':
      return <FormTextAreaField {...(props as FormInputFieldProps)} />;
    case 'alert-info':
      return <AlertInfo {...(props as AlertInfoProps & { name?: string })} />;
    case 'currency':
      return <FormCurrencyField {...(props as any)} />;
    case 'header-position-toggle':
      return (
        <FormHeaderPositionToggle
          {...(props as FormHeaderPositionToggleProps)}
        />
      );
    default:
      return <FormInputField {...(props as FormInputFieldProps)} />;
  }
};
